import { UmsMember, UmsMemberOperateHistory } from 'types/ums';
import LOCALS from 'commons/locals';
import { Trans } from 'react-i18next';
import { Button, Card, Modal, Table } from 'antd';
import usePagination from 'commons/hooks/usePagination';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageQuery } from 'types/base';
import { getMemberOperateHistoryList } from 'apis/ums';
import { ColumnsType } from 'antd/es/table';
import showTotal from 'components/show-total';
import formatTime from 'utils/formatTime';
import i18n from 'i18n';
import { History } from 'lucide-react';

type MemberOperateHistoryProps = {
  memberId: UmsMember['id'];
};

const OPERATE_TYPE_LABELS: Record<string, string> = {
  profile_update: '资料更新',
  tags_update: '标签更新',
  password_reset: '密码重置',
  account_delete: '账号注销',
  member_create: '创建会员',
  email_update: '修改邮箱',
};

const OPERATE_SOURCE_LABELS: Record<string, string> = {
  admin: '后台',
  website: '官网',
  app: 'App',
  system: '系统',
};

const formatChangeValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '-';
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '-';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const MemberOperateHistory = ({ memberId }: MemberOperateHistoryProps) => {
  const {
    loading,
    setLoading,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    dataSource,
    setDataSource,
  } = usePagination<UmsMemberOperateHistory>();

  const [detailRecord, setDetailRecord] =
    useState<UmsMemberOperateHistory | null>(null);

  const getDataSource = useCallback(
    async ({ pageNum, pageSize }: PageQuery) => {
      try {
        setLoading(true);
        const { list, total } = await getMemberOperateHistoryList({
          memberId,
          pageNum,
          pageSize,
        });

        setDataSource(list);
        setTotal(total);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [memberId, setDataSource, setLoading, setTotal],
  );

  useEffect(() => {
    getDataSource({ pageNum: 1, pageSize: 10 });
  }, [getDataSource]);

  const columns: ColumnsType<UmsMemberOperateHistory> = useMemo(
    () => [
      {
        title: <Trans i18nKey={LOCALS.operate_type} />,
        dataIndex: 'operateType',
        key: 'operateType',
        render: (operateType: string) =>
          OPERATE_TYPE_LABELS[operateType] || operateType,
      },
      {
        title: <Trans i18nKey={LOCALS.operator} />,
        dataIndex: 'operator',
        key: 'operator',
        render: (operator: string | null) => operator || '-',
      },
      {
        title: <Trans i18nKey={LOCALS.operate_source} />,
        dataIndex: 'operateSource',
        key: 'operateSource',
        render: (operateSource: string) =>
          OPERATE_SOURCE_LABELS[operateSource] || operateSource,
      },
      {
        title: <Trans i18nKey={LOCALS.change_details} />,
        dataIndex: 'changes',
        key: 'changes',
        render: (
          changes: UmsMemberOperateHistory['changes'],
          record: UmsMemberOperateHistory,
        ) => {
          if (!changes || changes.length === 0) {
            return '-';
          }
          return (
            <Button
              type="link"
              onClick={() => {
                setDetailRecord(record);
              }}
            >
              {i18n.t(LOCALS.view) || '查看'}（{changes.length}）
            </Button>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.remark} />,
        dataIndex: 'note',
        key: 'note',
        render: (note: string | null) =>
          note ? <div className="whitespace-pre-wrap">{note}</div> : '-',
      },
      {
        title: <Trans i18nKey={LOCALS.created_time} />,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: string) => formatTime(createTime),
      },
    ],
    [],
  );

  const changeDetailColumns = useMemo(
    () => [
      {
        title: <Trans i18nKey={LOCALS.field_name} />,
        dataIndex: 'field',
        key: 'field',
        width: 140,
        render: (field: string) => field || '-',
      },
      {
        title: <Trans i18nKey={LOCALS.old_value} />,
        dataIndex: 'oldValue',
        key: 'oldValue',
        render: (value: unknown) => (
          <span className="whitespace-pre-wrap break-all">
            {formatChangeValue(value)}
          </span>
        ),
      },
      {
        title: <Trans i18nKey={LOCALS.new_value} />,
        dataIndex: 'newValue',
        key: 'newValue',
        render: (value: unknown) => (
          <span className="whitespace-pre-wrap break-all">
            {formatChangeValue(value)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <Modal
        title={<Trans i18nKey={LOCALS.change_details} />}
        open={!!detailRecord}
        footer={null}
        width={720}
        onCancel={() => {
          setDetailRecord(null);
        }}
      >
        {detailRecord?.changes && (
          <Table
            bordered
            size="small"
            rowKey="field"
            pagination={false}
            dataSource={detailRecord.changes}
            columns={changeDetailColumns}
            scroll={{ y: '50vh' }}
          />
        )}
      </Modal>

      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <History className="w-5 h-5" />
        <Trans i18nKey={LOCALS.member_operate_history} />
      </div>

      <Table
        bordered
        pagination={{
          showTotal,
          total,
          pageSize,
          current: pageNum,
          onChange: (page, pageSize) => {
            setPageNum(page);
            setPageSize(pageSize);
            getDataSource({ pageNum: page, pageSize });
          },
        }}
        loading={loading}
        rowKey="id"
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        dataSource={dataSource}
        columns={columns}
      />
    </Card>
  );
};

export default MemberOperateHistory;
